<template>
  <div>
    <!-- 1. 右下角浮動小助手 -->
    <div v-if="isVisible" class="taro-assistant-wrapper" :class="snappedSide === 'left' ? 'align-left' : 'align-right'" :style="assistantStyle" ref="assistantRef">
      <transition name="bounce">
      <!-- 對話氣泡框 -->
      <div v-if="showBubble" class="assistant-bubble elevation-4">
        <div class="bubble-header d-flex align-center justify-space-between pb-2 mb-2 border-bottom">
          <div class="d-flex align-center primary--text text--darken-1 font-weight-bold text-body-2">
            <v-icon color="amber darken-3" size="18" class="mr-1">mdi-bell-ring-outline</v-icon>
            <span>小芋頭助手提醒</span>
          </div>
          <v-btn icon x-small color="grey darken-1" @click.stop="showBubble = false" title="收起提示">
            <v-icon size="16">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="bubble-body text-body-2 grey--text text--darken-4 mb-3 line-height-normal">
          您已有代理商/推廣商位階，但尚未填寫完整資料，請前往該頁面完成資料填寫以確保佣金正常發放。
        </div>

        <div class="bubble-footer d-flex justify-end">
          <v-btn
            small
            color="amber darken-3"
            dark
            elevation="2"
            class="font-weight-medium px-4 rounded-pill text-caption assistant-btn"
            @click="goToAgentForm"
          >
            前往填寫完整資料
            <v-icon right size="14">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
        <!-- 氣泡的小三角形指向上方或右側的頭像 -->
        <div class="bubble-arrow"></div>
      </div>
    </transition>

    <!-- 小助手頭像 (點擊可展開/收起氣泡) -->
    <div
      class="assistant-avatar-container"
      ref="avatarRef"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="onAvatarClick"
      title="點擊查看助手提醒"
    >
      <v-badge
        :value="!showBubble"
        color="red accent-2"
        dot
        overlap
        offset-x="10"
        offset-y="10"
      >
        <div class="avatar-bg d-flex align-center justify-center overflow-hidden">
          <video ref="dogVideo" :src="dogMp4" autoplay muted playsinline class="taro-avatar-video" @ended="onVideoEnded"></video>
        </div>
      </v-badge>
    </div>
  </div>

  <!-- 2. 強制更新真實姓名 Dialog (為空時) -->
  <v-dialog
    v-model="showUpdateNameDialog"
    persistent
    max-width="480"
    content-class="rounded-16 name-check-dialog"
  >
    <v-card class="pa-6 rounded-16 elevation-12">
      <div class="text-center mb-4">
        <v-avatar color="red lighten-5" size="64" class="mb-3">
          <v-icon color="red darken-2" size="36">mdi-account-alert-outline</v-icon>
        </v-avatar>
        <div class="text-h5 font-weight-bold grey--text text--darken-4">請填寫真實姓名</div>
        <div class="text-body-2 grey--text text--darken-1 mt-1">
          為了保障您的帳戶安全與各項服務權益，請務必填寫您的真實姓名。
        </div>
      </div>

      <v-form ref="updateNameForm" @submit.prevent="submitUpdateName">
        <div class="mb-4">
          <div class="text-subtitle-2 font-weight-bold mb-2 grey--text text--darken-3">
            真實姓名 <span class="red--text">*</span>
          </div>
          <v-text-field
            v-model="inputName"
            placeholder="請輸入真實姓名"
            outlined
            dense
            hide-details="auto"
            :rules="[
              (v) => !!(v && v.trim()) || '真實姓名為必填',
              (v) => (v && v.trim().length >= 2) || '至少輸入 2 個字',
              (v) => !/\d/.test(v) || '真實姓名不可包含數字'
            ]"
          ></v-text-field>
        </div>

        <!-- 當填入非純中文時，顯示確認勾選框 -->
        <div v-if="isInputNonChinese" class="bg-grey-lighten pa-3 rounded-lg mb-4 d-flex align-center">
          <v-checkbox
            v-model="confirmAsRealName"
            label="正是我的真實姓名 (確認後不再提醒)"
            hide-details
            class="mt-0 pt-0"
            color="blue darken-2"
            :rules="[(v) => !!v || '請確認此為您的真實姓名']"
          ></v-checkbox>
        </div>

        <div class="d-flex justify-end mt-6">
          <v-btn
            color="primary"
            elevation="2"
            height="44"
            class="px-6 rounded-pill font-weight-medium w-100"
            type="submit"
            :loading="saveNameLoading"
          >
            確認更新
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>

  <!-- 3. 非中文姓名確認 Dialog -->
  <v-dialog
    v-model="showConfirmNameDialog"
    persistent
    max-width="480"
    content-class="rounded-16 name-check-dialog"
  >
    <v-card class="pa-6 rounded-16 elevation-12">
      <div class="text-center mb-4">
        <v-avatar color="blue lighten-5" size="64" class="mb-3">
          <v-icon color="blue darken-2" size="36">mdi-account-question-outline</v-icon>
        </v-avatar>
        <div class="text-h5 font-weight-bold grey--text text--darken-4 mb-4">非中文姓名確認</div>
        <div class="text-body-2 grey--text text--darken-1 mt-2">
          <div>系統偵測到您的真實姓名包含非中文格式：</div>
          <div class="text-subtitle-1 font-weight-black black--text py-2">{{ originalName }}</div>
          <div>為確保佣金能正常發放，請確認此是否為您的真實姓名。</div>
        </div>
      </div>

      <v-form ref="confirmNameForm" @submit.prevent="submitConfirmName">
        <div class="bg-grey-lighten pa-3 rounded-lg mb-6 d-flex align-center">
          <v-checkbox
            v-model="confirmAsRealName"
            label="正是我的真實姓名 (確認後不再提醒)"
            hide-details
            class="mt-0 pt-0"
            color="blue darken-2"
            :rules="[(v) => !!v || '請確認此為您的真實姓名']"
          ></v-checkbox>
        </div>

        <div class="d-flex justify-space-between mt-6">
          <v-btn
            text
            color="grey darken-1"
            height="44"
            class="px-4 font-weight-medium"
            @click="openUpdateNameDialog"
          >
            我要修改
          </v-btn>
          <v-btn
            color="blue darken-2"
            dark
            elevation="2"
            height="44"
            class="px-6 rounded-pill font-weight-medium"
            type="submit"
          >
            確定
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import { Grade_Overview, Profile_Get, Profile_Post } from "@/apis/api";
import { showsAppTabBar } from "@/utils/appTabBar";

export default {
  name: "GlobalAgentNotification",
  data() {
    return {
      isAgentIncomplete: false,
      showBubble: true,
      checkLoading: false,
      dogMp4: require("@/assets/dog_mp4.mp4"),
      windowWidth: window.innerWidth,

      // 真實姓名檢查相關
      showUpdateNameDialog: false,
      showConfirmNameDialog: false,
      inputName: "",
      originalName: "",
      confirmAsRealName: false,
      saveNameLoading: false,

      // 拖曳位置與貼邊相關
      position: {
        x: null,
        y: null,
      },
      snappedSide: "right",
      isDragging: false,
      dragged: false,
    };
  },
  computed: {
    signinStatus() {
      return this.$store.state.signinStatus;
    },
    userGuid() {
      return this.$store.state.userGuid || localStorage.getItem("_signinUserGuid");
    },
    /*
      目前登入的帳號，用來查、改真實姓名。要跟著 store 走，不能讀 localStorage 的 _rawEmail：
      computed 不會因為 localStorage 改變而重算，換帳號後會一直拿上一個帳號去查、去改
      （2026-10-30 App 裡登入本人帳號，卻跳出另一個帳號的姓名確認）。
      store 的 userRealEmail 是 SyncFullProfile 向後端問來的，登出時清掉。
    */
    userEmail() {
      return this.$store.state.userRealEmail;
    },
    isVisible() {
      return this.signinStatus && this.isAgentIncomplete;
    },
    isInputNonChinese() {
      if (!this.inputName) return false;
      const clean = this.inputName.trim();
      if (!clean) return false;
      return !/^[\u4e00-\u9fa5]+$/.test(clean);
    },
    assistantStyle() {
      let bottomOffset = 32;
      const isMobile = this.windowWidth <= 600;

      if (isMobile) {
        // 手機版基本底距先拉高至 84px (避開瀏覽器預設導覽列或一般的底部元件)
        bottomOffset = 84;

        const routeName = this.$route.name || "";
        const routePath = this.$route.path || "";

        // 涵蓋所有已知或潛在有底部按鈕/導覽列的頁面
        const hasBottomBarRoutes = [
          "Product",
          "ProductDetail",
          "Cart",
          "Checkout",
          "Project",
          "Yentity",
          "Outfit",
          "Center",
          "Member",
          "Upgrade",
        ];

        if (
          hasBottomBarRoutes.includes(routeName) ||
          routePath.includes("/product") ||
          routePath.includes("/project") ||
          routePath.includes("/center")
        ) {
          bottomOffset = 40; // 進一步提高至 116px，確保底部交互區域完全不受阻擋
        }

        // 有底部導覽列（56px，utils/appTabBar.js）的頁面，頭像要在它上面，不然會蓋住最右邊的「個人中心」
        if (showsAppTabBar(this.$route, this.$store.state.signinStatus)) {
          bottomOffset = 84;
        }
      }

      if (this.position.x === null || this.position.y === null) {
        return {
          bottom: `${bottomOffset}px`,
          right: isMobile ? "20px" : "32px",
          transition: "bottom 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), right 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)",
        };
      }

      if (this.snappedSide === "left") {
        return {
          left: `${this.position.x}px`,
          bottom: `${this.position.y}px`,
          top: "auto",
          right: "auto",
          transition: this.isDragging
            ? "none"
            : "left 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), bottom 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)",
        };
      } else {
        const elementWidth = isMobile ? 56 : 72;
        const rightValue = window.innerWidth - this.position.x - elementWidth;
        return {
          right: `${rightValue}px`,
          bottom: `${this.position.y}px`,
          top: "auto",
          left: "auto",
          transition: this.isDragging
            ? "none"
            : "right 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), bottom 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)",
        };
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.$root.$on("profile-updated", this.checkProfileName);
    this.loadSavedPosition();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.$root.$off("profile-updated", this.checkProfileName);
    if (this._videoTimer) {
      clearTimeout(this._videoTimer);
    }
    window.removeEventListener("touchmove", this.onDragging);
    window.removeEventListener("touchend", this.onDragEnd);
    window.removeEventListener("mousemove", this.onDragging);
    window.removeEventListener("mouseup", this.onDragEnd);
  },
  watch: {
    signinStatus: {
      immediate: true,
      handler(val) {
        if (val) {
          this.checkAgentStatus();
          this.checkProfileName();
          this.loadSavedPosition();
        } else {
          this.isAgentIncomplete = false;
          this.showUpdateNameDialog = false;
          this.showConfirmNameDialog = false;
        }
      },
    },
    userGuid(val) {
      if (val && this.signinStatus) {
        this.checkAgentStatus();
      }
    },
    userEmail(val) {
      if (val && this.signinStatus) {
        this.checkProfileName();
        this.loadSavedPosition();
      }
    },
    $route() {
      if (this.signinStatus) {
        if (this.userGuid) this.checkAgentStatus();
        if (this.userEmail) this.checkProfileName();
      }
    },
  },
  methods: {
    // #region 真實姓名檢查與更新邏輯
    async checkProfileName() {
      const email = this.userEmail;
      if (!email) return;

      if (localStorage.getItem("_confirmedRealName_" + email) === "true") {
        this.showUpdateNameDialog = false;
        this.showConfirmNameDialog = false;
        return;
      }

      try {
        const res = await Profile_Get(email);
        // 等回應的期間換了帳號：這份是上一個帳號的，不要拿來顯示
        if (email !== this.userEmail) return;
        const data = res?.data?.data;
        if (!data) return;

        const nameVal = data.name;

        // 嚴格判斷 null、undefined 或 trim() 空字串
        if (nameVal === null || nameVal === undefined || String(nameVal).trim() === "") {
          this.inputName = "";
          this.originalName = "";
          this.showConfirmNameDialog = false;
          this.showUpdateNameDialog = true;
        } else {
          const cleanName = String(nameVal).trim();
          this.originalName = cleanName;
          const isAllChinese = /^[\u4e00-\u9fa5]+$/.test(cleanName);
          if (!isAllChinese) {
            this.confirmAsRealName = false;
            this.showUpdateNameDialog = false;
            this.showConfirmNameDialog = true;
          } else {
            this.showUpdateNameDialog = false;
            this.showConfirmNameDialog = false;
          }
        }
      } catch (err) {
        console.warn("[GlobalAgentNotification] 取得 Profile 失敗", err);
      }
    },

    openUpdateNameDialog() {
      this.showConfirmNameDialog = false;
      this.inputName = this.originalName;
      this.showUpdateNameDialog = true;
    },

    async submitConfirmName() {
      if (!this.$refs.confirmNameForm.validate()) return;
      const email = this.userEmail;
      if (email) {
        localStorage.setItem("_confirmedRealName_" + email, "true");
      }
      this.showConfirmNameDialog = false;
      this.$notify({ content: "已確認真實姓名", status: "success" });
    },

    async submitUpdateName() {
      if (!this.$refs.updateNameForm.validate()) return;
      this.saveNameLoading = true;
      const email = this.userEmail;
      const newName = this.inputName.trim();

      const formData = new FormData();
      formData.append("Email", email);
      formData.append("Name", newName);

      try {
        await Profile_Post(formData);
        
        // 如果使用者輸入了非中文姓名且勾選了確認，直接寫入 localStorage 避免再次跳出 Dialog 2
        if (this.isInputNonChinese && this.confirmAsRealName && email) {
          localStorage.setItem("_confirmedRealName_" + email, "true");
        }

        this.showUpdateNameDialog = false;
        this.$notify({ content: "真實姓名更新成功", status: "success" });
        // 更新成功後，發送事件讓元件重新檢查是否為中文
        this.$root.$emit("profile-updated");
      } catch (err) {
        console.error("[GlobalAgentNotification] 更新姓名失敗", err);
        this.$notify({ content: "姓名更新失敗，請稍後再試", status: "error" });
      } finally {
        this.saveNameLoading = false;
      }
    },
    // #endregion
    async checkAgentStatus() {
      if (!this.userGuid || this.checkLoading) return;
      this.checkLoading = true;
      try {
        const res = await Grade_Overview(this.userGuid);
        const data = res?.data;
        if (!data) {
          this.isAgentIncomplete = false;
          return;
        }

        const status = data.status;
        if (status !== 2 && status !== 3) {
          this.isAgentIncomplete = false;
          return;
        }

        const form = status === 2 ? data.application : data.contractProfile;
        if (!form) {
          this.isAgentIncomplete = true;
          this.showBubble = true;
          return;
        }

        const isPromo = !!(data.isPromotionCenter || form.isPromotionCenter);
        const isEmpty = (val) => val === null || val === undefined || String(val).trim() === "";

        let isIncomplete = false;

        if (isPromo) {
          // Form2 推廣中心必填欄位
          const requiredFields = [
            "centerName",
            "ownerName",
            "ownerId",
            "centerPhone",
            "bankCode",
            "bankAccount",
            "city",
            "district",
            "address",
            "adminName",
            "adminPhone",
            "adminEmail",
          ];
          for (const field of requiredFields) {
            if (isEmpty(form[field])) {
              isIncomplete = true;
              break;
            }
          }
          if (!isIncomplete) {
            const hasBankbook = !isEmpty(form.imageBankBookId) || !isEmpty(form.bankbookId) || !isEmpty(form.bankbookUrl) || !isEmpty(form.bankbook);
            const hasCert = !isEmpty(form.imageCertificateId) || !isEmpty(form.certId) || !isEmpty(form.certUrl) || !isEmpty(form.cert);
            const hasIdFront = !isEmpty(form.imageIdFrontId) || !isEmpty(form.idFrontId) || !isEmpty(form.idFrontUrl) || !isEmpty(form.idFront);
            const hasIdBack = !isEmpty(form.imageIdBackId) || !isEmpty(form.idBackId) || !isEmpty(form.idBackUrl) || !isEmpty(form.idBack);

            if (!hasBankbook || !hasCert || !hasIdFront || !hasIdBack) {
              isIncomplete = true;
            }
          }
        } else {
          // Form1 一般代理商/介紹人必填欄位
          const nowGradeId = data.contract?.currentPayGradeDefinitionId || data.application?.payGradeDefinitionId || 0;
          if (nowGradeId === 1 && isEmpty(form.unitName)) {
            isIncomplete = true;
          } else {
            const requiredFields = [
              "realName",
              "idNumber",
              "mobile",
              "bankCode",
              "bankAccount",
              "city",
              "district",
              "address",
              "contactName",
              "relationship",
              "contactPhone",
            ];
            for (const field of requiredFields) {
              if (isEmpty(form[field])) {
                isIncomplete = true;
                break;
              }
            }
            if (!isIncomplete) {
              const hasBankbook = !isEmpty(form.imageBankBookId) || !isEmpty(form.bankbookId) || !isEmpty(form.bankbookUrl) || !isEmpty(form.bankbook);
              const hasIdFront = !isEmpty(form.imageIdFrontId) || !isEmpty(form.idFrontId) || !isEmpty(form.idFrontUrl) || !isEmpty(form.idFront);
              const hasIdBack = !isEmpty(form.imageIdBackId) || !isEmpty(form.idBackId) || !isEmpty(form.idBackUrl) || !isEmpty(form.idBack);

              if (!hasBankbook || !hasIdFront || !hasIdBack) {
                isIncomplete = true;
              }
            }
          }
        }

        this.isAgentIncomplete = isIncomplete;
        if (isIncomplete && this.showBubble === false && !this._hasCheckedOnce) {
          this.showBubble = true;
        }
        this._hasCheckedOnce = true;
      } catch (err) {
        console.warn("[GlobalAgentNotification] 取得 Grade_Overview 失敗", err);
      } finally {
        this.checkLoading = false;
      }
    },
    toggleBubble() {
      this.showBubble = !this.showBubble;
    },
    goToAgentForm() {
      if (this.$route.name !== "Center" || this.$route.query.tab !== "agent") {
        this.$router.push({ name: "Center", query: { tab: "agent", form: 1 } }).catch(() => {});
      } else {
        this.$router.replace({ query: { ...this.$route.query, form: 1 } }).catch(() => {});
      }
      this.showBubble = false;
    },
    onVideoEnded() {
      const video = this.$refs.dogVideo;
      if (!video) return;
      this._videoTimer = setTimeout(() => {
        if (video && typeof video.play === "function") {
          video.currentTime = 0;
          video.play().catch(() => {});
        }
      }, 3000);
    },
    onResize() {
      this.windowWidth = window.innerWidth;
      if (this.position.x !== null && this.position.y !== null) {
        this.snapToEdge(this.position.x, this.position.y, this.snappedSide);
      }
    },
    startDrag(e) {
      // 1. Differentiate mouse vs touch
      const isTouch = e.type.startsWith("touch");
      const event = isTouch ? e.touches[0] : e;

      // 2. Clear transition during drag
      this.isDragging = true;
      this.dragged = false;

      // 3. Get the avatar container's current position on screen
      const el = this.$refs.avatarRef;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // 4. Calculate mouse pointer offset relative to the avatar's left and bottom
      this._dragStartOffset = {
        x: event.clientX - rect.left,
        y: rect.bottom - event.clientY,
      };

      // Record starting coordinate to detect drag threshold
      this._dragStartCoord = {
        x: event.clientX,
        y: event.clientY,
      };

      // 5. Register global movement and end listeners
      if (isTouch) {
        window.addEventListener("touchmove", this.onDragging, { passive: false });
        window.addEventListener("touchend", this.onDragEnd);
      } else {
        window.addEventListener("mousemove", this.onDragging);
        window.addEventListener("mouseup", this.onDragEnd);
      }
    },
    onDragging(e) {
      if (!this.isDragging) return;

      const isTouch = e.type.startsWith("touch");
      const event = isTouch ? e.touches[0] : e;

      // Prevent window scrolling on touch screens when dragging
      if (isTouch) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }

      const clientX = event.clientX;
      const clientY = event.clientY;

      let avatarLeft = clientX - this._dragStartOffset.x;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      let avatarBottomFromViewportBottom = windowHeight - (clientY + this._dragStartOffset.y);

      const isMobile = windowWidth <= 600;
      const elementWidth = isMobile ? 56 : 72;
      const elementHeight = isMobile ? 56 : 72;

      // Keep avatar inside screen boundaries (with small padding)
      const dragMargin = 5;
      if (avatarLeft < dragMargin) avatarLeft = dragMargin;
      if (avatarLeft > windowWidth - elementWidth - dragMargin) {
        avatarLeft = windowWidth - elementWidth - dragMargin;
      }
      if (avatarBottomFromViewportBottom < dragMargin) avatarBottomFromViewportBottom = dragMargin;
      if (avatarBottomFromViewportBottom > windowHeight - elementHeight - dragMargin) {
        avatarBottomFromViewportBottom = windowHeight - elementHeight - dragMargin;
      }

      this.position.x = avatarLeft;
      this.position.y = avatarBottomFromViewportBottom;

      // Check drag threshold (5px)
      const dx = clientX - this._dragStartCoord.x;
      const dy = clientY - this._dragStartCoord.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 5) {
        this.dragged = true;
      }
    },
    onDragEnd(e) {
      if (!this.isDragging) return;
      this.isDragging = false;

      // Clean up global listeners
      window.removeEventListener("touchmove", this.onDragging);
      window.removeEventListener("touchend", this.onDragEnd);
      window.removeEventListener("mousemove", this.onDragging);
      window.removeEventListener("mouseup", this.onDragEnd);

      if (this.dragged) {
        if (e && typeof e.preventDefault === "function") {
          e.preventDefault();
        }
        // Snap to nearest edge
        this.snapToEdge(this.position.x, this.position.y);
      }
    },
    snapToEdge(x, y, forceSide = null) {
      const isMobile = window.innerWidth <= 600;
      const margin = isMobile ? 20 : 32;
      const elementWidth = isMobile ? 56 : 72;
      const elementHeight = isMobile ? 56 : 72;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let side = forceSide;
      if (!side) {
        const midPoint = windowWidth / 2;
        side = (x + elementWidth / 2 > midPoint) ? "right" : "left";
      }

      let finalX = (side === "left") ? margin : (windowWidth - elementWidth - margin);

      let finalY = y;
      if (finalY < margin) {
        finalY = margin;
      } else if (finalY > windowHeight - elementHeight - margin) {
        finalY = windowHeight - elementHeight - margin;
      }

      this.snappedSide = side;
      this.position.x = finalX;
      this.position.y = finalY;

      const email = this.userEmail || "global";
      localStorage.setItem(
        `_taro_assistant_pos_${email}`,
        JSON.stringify({ x: finalX, y: finalY, snappedSide: side })
      );
    },
    loadSavedPosition() {
      const email = this.userEmail || "global";
      const saved = localStorage.getItem(`_taro_assistant_pos_${email}`);
      if (saved) {
        try {
          const { x, y, snappedSide } = JSON.parse(saved);
          this.snapToEdge(x, y, snappedSide);
        } catch (e) {
          console.warn("Failed to load saved position", e);
        }
      } else {
        this.position.x = null;
        this.position.y = null;
        this.snappedSide = "right";
      }
    },
    onAvatarClick(e) {
      if (this.dragged) {
        this.dragged = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.toggleBubble();
    },
  },

};
</script>

<style scoped lang="scss">
/* 右下角浮動容器 */
.taro-assistant-wrapper {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  pointer-events: none; /* 讓容器本身不阻擋點擊 */
}

.taro-assistant-wrapper.align-right {
  align-items: flex-end;
}
.taro-assistant-wrapper.align-right .bubble-arrow {
  right: 28px;
  left: auto;
}

.taro-assistant-wrapper.align-left {
  align-items: flex-start;
}
.taro-assistant-wrapper.align-left .bubble-arrow {
  left: 28px;
  right: auto;
}

/* 讓內部元素恢復點擊事件 */
.assistant-bubble,
.assistant-avatar-container {
  pointer-events: auto;
}

/* 對話氣泡框設計 (Glassmorphism & 現代陰影) */
.assistant-bubble {
  width: 280px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 14px;
  position: relative;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  animation: float-bubble 3s ease-in-out infinite alternate;
}

/* 氣泡下方指向頭像的小三角形 */
.bubble-arrow {
  position: absolute;
  bottom: -8px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid rgba(255, 255, 255, 0.95);
  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.05));
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.line-height-normal {
  line-height: 1.5;
}

.assistant-btn {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 143, 0, 0.3);
  }
  &:active {
    transform: translateY(0);
  }
}

/* 小助手頭像容器 */
.assistant-avatar-container {
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &:hover {
    transform: scale(1.1);
    .avatar-bg {
      box-shadow: 0 8px 20px rgba(108, 77, 246, 0.25);
    }
  }
  &:active {
    transform: scale(0.95);
  }
}

/* 頭像圓形無邊界外框 */
.avatar-bg {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
}

.taro-avatar-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
}

/* 動畫 */
@keyframes float-bubble {
  0% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(-4px);
  }
}

/* Vue 氣泡過渡動畫 */
.bounce-enter-active {
  animation: bounce-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.bounce-leave-active {
  animation: bounce-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) reverse;
}
@keyframes bounce-in {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 手機版適配 */
@media (max-width: 600px) {
  .assistant-bubble {
    width: 250px;
  }
  .avatar-bg {
    width: 56px;
    height: 56px;
  }
  .taro-assistant-wrapper.align-right .bubble-arrow {
    right: 24px;
    left: auto;
  }
  .taro-assistant-wrapper.align-left .bubble-arrow {
    left: 24px;
    right: auto;
  }
}
</style>

<style lang="scss">
/*
  其他元件要暫時藏起助手時，在 body 加 taro-assistant-hidden。
  助手的 z-index 是 9999，會浮在抽屜、底部面板上面（例如個人中心的設定抽屜）。
*/
body.taro-assistant-hidden .taro-assistant-wrapper {
  visibility: hidden;
}
</style>
